import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  Fade,
  Slide,
  Chip,
  Stack,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import BusinessIcon from '@mui/icons-material/Business';
import PetsIcon from '@mui/icons-material/Pets';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import FlightIcon from '@mui/icons-material/Flight';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import PublicIcon from '@mui/icons-material/Public';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import NatureIcon from '@mui/icons-material/Nature';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import useCategories from '../../hooks/useCategories';
import LogoSection from '../common/LogoSection';


const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [topBarVisible, setTopBarVisible] = useState(true);
  const [toursExpanded, setToursExpanded] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const scrollDirection = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Dynamic tour categories from backend
  const { categories: tourCategories } = useCategories();

  // Map category names to icons
  const getCategoryIcon = (name) => {
    const lower = name.toLowerCase();
    if (lower === 'domestic') return <FlightIcon fontSize="small" />;
    if (lower === 'international') return <PublicIcon fontSize="small" />;
    if (lower === 'religious') return <TempleHinduIcon fontSize="small" />;
    if (lower === 'wildlife') return <NatureIcon fontSize="small" />;
    return <FlightTakeoffIcon fontSize="small" />;
  };

  const getCategoryDesc = (name) => {
    const lower = name.toLowerCase();
    if (lower === 'domestic') return 'Explore India';
    if (lower === 'international') return 'Worldwide tours';
    if (lower === 'religious') return 'Spiritual journeys';
    if (lower === 'wildlife') return 'Nature & safari';
    return `${name} tours`;
  };

  // Scroll handler with throttling via requestAnimationFrame
  const handleScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;

    const SCROLL_DEAD_ZONE = 15;
    const TOP_BAR_THRESHOLD = 80;
    const SCROLLED_THRESHOLD = 50;

    requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;

      // Update scrolled state
      setScrolled(currentScrollY > SCROLLED_THRESHOLD);

      // Determine scroll direction with dead zone
      const delta = currentScrollY - lastScrollY.current;
      let newDirection = scrollDirection.current;

      if (Math.abs(delta) > SCROLL_DEAD_ZONE) {
        newDirection = delta > 0 ? 'down' : 'up';
        scrollDirection.current = newDirection;
      }

      // Show/hide top bar based on direction
      if (currentScrollY > TOP_BAR_THRESHOLD) {
        if (newDirection === 'down') {
          setTopBarVisible(false);
        } else if (newDirection === 'up') {
          setTopBarVisible(true);
        }
      } else {
        setTopBarVisible(true);
      }

      lastScrollY.current = currentScrollY;
      ticking.current = false;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleToursClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleToursClose = (category) => {
    setAnchorEl(null);
    if (category) {
      navigate(`/tours?category=${category}`);
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const menuItems = [
    { label: 'Home', path: '/', icon: <HomeIcon /> },
    { label: 'About Us', path: '/about', icon: <InfoIcon /> },
    { 
      label: 'Tours', 
      path: '/tours',
      icon: <FlightTakeoffIcon />,
      hasDropdown: true,
      badge: 'New',
      dropdownItems: tourCategories.map((cat) => ({
        label: cat,
        value: cat,
        icon: getCategoryIcon(cat),
        desc: getCategoryDesc(cat),
      }))
    },
    { label: 'Services', path: '/services', icon: <BuildCircleIcon /> },
    { label: 'Corporate Booking', path: '/corporate-booking', icon: <BusinessIcon /> },
    { label: 'Wild Life Tours', path: '/wildlife-tours', icon: <PetsIcon /> },
    { label: 'Contact Us', path: '/contact', icon: <ContactMailIcon /> }
  ];

  // Mobile Drawer Content
  const drawer = (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      background: 'linear-gradient(180deg, #ffffff 0%, #F7F6F2 100%)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Drawer Header */}
      <Box sx={{ 
        p: { xs: 2, sm: 2.5 }, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(135deg, #0C2340 0%, #1A3365 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -50,
          right: -50,
          width: 100,
          height: 100,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
          <LogoSection
            variant="default"
            size="small"
            showText={false}
          />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              color: '#ffffff',
              letterSpacing: '0.5px',
            }}
          >
            Menu
          </Typography>
        </Box>
        <IconButton 
          onClick={() => setMobileOpen(false)} 
          sx={{ 
            color: 'white', 
            position: 'relative', 
            zIndex: 1,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Navigation Links */}
      <List sx={{ pt: 2, px: 1, flex: 1, overflow: 'auto' }}>
        {menuItems.map((item) => (
          item.hasDropdown ? (
            <Box key={item.label}>
              <ListItem disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => setToursExpanded(!toursExpanded)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    bgcolor: toursExpanded ? 'rgba(12, 35, 64, 0.06)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(12, 35, 64, 0.08)' },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.badge && (
                      <Chip 
                        label={item.badge} 
                        size="small" 
                        sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white',
                          height: 20,
                          fontSize: '0.6rem',
                          fontWeight: 700
                        }} 
                      />
                    )}
                    <ExpandMoreIcon 
                      sx={{ 
                        color: 'text.secondary',
                        transform: toursExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                      }} 
                    />
                  </Box>
                </ListItemButton>
              </ListItem>
              <Collapse in={toursExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 3, pr: 1, pb: 1 }}>
                  {/* "View All Tours" button */}
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => { setMobileOpen(false); navigate('/tours'); }}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { 
                          bgcolor: 'primary.dark',
                          transform: 'translateX(4px)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: 'white' }}>
                        <FlightTakeoffIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="View All Tours"
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 700 }}
                      />
                    </ListItemButton>
                  </ListItem>
                  {item.dropdownItems.map((dropdownItem) => (
                    <ListItem key={dropdownItem.value} disablePadding>
                      <ListItemButton
                        onClick={() => { 
                          setMobileOpen(false); 
                          navigate(`/tours?category=${dropdownItem.value}`); 
                        }}
                        sx={{
                          borderRadius: 2,
                          mb: 0.3,
                          py: 0.8,
                          '&:hover': { 
                            bgcolor: 'rgba(12, 35, 64, 0.06)',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                          {dropdownItem.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={dropdownItem.label}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondary={dropdownItem.desc}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </Box>
              </Collapse>
            </Box>
          ) : (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                onClick={() => { setMobileOpen(false); navigate(item.path); }}
                selected={isActive(item.path)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(12, 35, 64, 0.12)',
                    '&:hover': { bgcolor: 'rgba(12, 35, 64, 0.18)' },
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                    }
                  },
                  '&:hover': { bgcolor: 'rgba(12, 35, 64, 0.06)' },
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40, 
                  color: isActive(item.path) ? 'primary.main' : 'text.secondary' 
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{ 
                    fontWeight: isActive(item.path) ? 700 : 500,
                    fontSize: '0.95rem',
                    color: isActive(item.path) ? 'primary.main' : 'text.primary'
                  }}
                />
                {item.badge && (
                  <Chip 
                    label={item.badge} 
                    size="small" 
                    sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      height: 20,
                      fontSize: '0.6rem',
                      fontWeight: 700
                    }} 
                  />
                )}
              </ListItemButton>
            </ListItem>
          )
        ))}
      </List>

      {/* Drawer Footer with Contact Info */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(12, 35, 64, 0.03)',
      }}>
        <Stack spacing={1.5}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<PhoneOutlinedIcon />}
            href={`tel:${process.env.REACT_APP_PHONE_NUMBER}`}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              py: 1.2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {process.env.REACT_APP_PHONE_NUMBER || '1800-2099-100'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<WhatsAppIcon />}
            href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER?.replace(/\+/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#25D366',
              borderColor: '#25D366',
              '&:hover': { 
                borderColor: '#25D366',
                bgcolor: 'rgba(37, 211, 102, 0.04)',
              },
              py: 1.2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            WhatsApp
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<EmailOutlinedIcon />}
            href={`mailto:${process.env.REACT_APP_EMAILID}`}
            sx={{
              py: 1.2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {process.env.REACT_APP_EMAILID || 'info@travelagency.com'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Top Bar - Hidden on mobile, slides on desktop */}
      <Slide direction="down" in={topBarVisible && !isMobile} timeout={250}>
        <Box 
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            px: { md: 2, lg: 3 },
            backgroundColor: 'white',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            color: 'text.secondary',
          }}
        >
          <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Left side: Phone & Email */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box 
                component="a"
                href={`tel:${process.env.REACT_APP_PHONE_NUMBER}`}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: 'text.primary',
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s ease',
                  cursor: 'pointer'
                }}
              >
                <PhoneOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: { md: '0.8rem', lg: '0.9rem' },
                    letterSpacing: '0.5px'
                  }}
                >
                  {process.env.REACT_APP_PHONE_NUMBER || '1800-2099-100'}
                </Typography>
                <Chip 
                  label="24/7" 
                  size="small"
                  sx={{ 
                    height: 18, 
                    fontSize: '0.6rem',
                    bgcolor: 'success.light',
                    color: 'success.dark',
                    fontWeight: 600,
                    display: { md: 'none', lg: 'inline-flex' }
                  }}
                />
              </Box>
              <Divider orientation="vertical" flexItem sx={{ height: 16 }} />
              <Box 
                component="a"
                href={`mailto:${process.env.REACT_APP_EMAILID}`}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  cursor: 'pointer',
                  color: 'text.primary',
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s ease',
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { md: '0.75rem', lg: '0.85rem' } }}>
                  {process.env.REACT_APP_EMAILID || 'info@travelagency.com'}
                </Typography>
              </Box>
            </Stack>

            {/* Right side: WhatsApp & Help */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box 
                component="a"
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER?.replace(/\+/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  cursor: 'pointer',
                  color: 'text.primary',
                  textDecoration: 'none',
                  '&:hover': { color: '#25D366' },
                  transition: 'color 0.2s ease',
                }}
              >
                <HeadsetMicOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { md: '0.75rem', lg: '0.85rem' } }}>
                  WhatsApp
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ height: 16 }} />

              <Box 
                onClick={() => navigate('/contact')}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s ease',
                }}
              >
                <PhoneOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { md: '0.75rem', lg: '0.85rem' } }}>
                  Help
                </Typography>
              </Box>
            </Stack>
          </Container>
        </Box>
      </Slide>

      {/* Main Navbar - Always Visible */}
      <AppBar 
        position="sticky" 
        elevation={scrolled ? 4 : 0}
        sx={{ 
          backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'white',
          color: 'primary.main',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          boxShadow: scrolled ? '0px 2px 12px rgba(0,0,0,0.08)' : 'none',
          borderBottom: scrolled ? 'none' : '1px solid rgba(0,0,0,0.04)',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            sx={{ 
              px: { xs: 1, sm: 2, md: 0 },
              minHeight: { xs: 64, sm: 64, md: 72 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: { xs: 0, md: 2 },
            }}
          >
            {/* Desktop Logo - Hidden on mobile */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              alignItems: 'center',
              flexShrink: 0,
              mr: { md: 2, lg: 4 }
            }}>
              <LogoSection
                variant="default"
                size={isMobile ? "small" : "medium"}
                textSx={{
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                }}
              />
            </Box>

            {/* ============ MOBILE LAYOUT: Single Row [ 🧳 Logo | 📞 | ☰ ] ============ */}
<Box sx={{
  display: { xs: 'flex', md: 'none' },
  alignItems: 'center',
  width: '100%',
  justifyContent: 'space-between',
  gap: 0.5,
}}>
  {/* Logo - Left side, full text, no truncation */}
  <Box sx={{ 
    flex: 1,
    display: 'flex', 
    alignItems: 'center',
    minWidth: 0,
    mr: 0.5,
  }}>
    <LogoSection
      variant="default"
      size="small"
      textSx={{
        whiteSpace: 'nowrap',
        fontSize: { xs: '0.7rem', sm: '0.85rem' },
        fontWeight: 700,
      }}
    />
  </Box>

  {/* Right side icons group */}
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
    {/* Phone icon */}
    <IconButton
      component="a"
      href={`tel:${process.env.REACT_APP_PHONE_NUMBER}`}
      size="small"
      sx={{
        color: 'primary.main',
        bgcolor: 'rgba(12, 35, 64, 0.06)',
        '&:hover': { bgcolor: 'rgba(12, 35, 64, 0.12)' },
      }}
    >
      <PhoneOutlinedIcon sx={{ fontSize: 20 }} />
    </IconButton>

    {/* Menu icon */}
    <IconButton 
      onClick={() => setMobileOpen(!mobileOpen)}
      size="large"
      color="inherit"
      aria-label="open drawer"
      sx={{
        '&:hover': { bgcolor: 'rgba(12, 35, 64, 0.08)' },
      }}
    >
      <MenuIcon />
    </IconButton>
  </Box>
</Box>
            {/* ============ END MOBILE LAYOUT ============ */}

            {/* Desktop Navigation */}
            <Box 
              sx={{ 
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: { md: 0, lg: 0.5 },
                flexGrow: 1,
                justifyContent: 'center',
                flexWrap: 'nowrap',
              }}
            >
              {menuItems.map((item) => (
                item.hasDropdown ? (
                  <Box key={item.label}>
                    <Button
                      onClick={handleToursClick}
                      endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        color: isActive('/tours') ? 'primary.main' : 'text.secondary',
                        fontWeight: isActive('/tours') ? 700 : 500,
                        px: { md: 1, lg: 1.8 },
                        py: 0.8,
                        borderRadius: 2,
                        fontSize: { md: '0.8rem', lg: '0.9rem' },
                        position: 'relative',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        '&::after': isActive('/tours') ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 2,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '40%',
                          height: 3,
                          borderRadius: 1.5,
                          bgcolor: 'primary.main',
                        } : {},
                        '&:hover': {
                          bgcolor: 'rgba(12, 35, 64, 0.06)',
                          color: 'primary.main',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {item.label}
                      {item.badge && (
                        <Chip 
                          label={item.badge}
                          size="small"
                          sx={{ 
                            height: 16,
                            fontSize: '0.55rem',
                            bgcolor: 'error.main',
                            color: 'white',
                            fontWeight: 700,
                            ml: 0.5,
                            display: { md: 'none', lg: 'inline-flex' }
                          }}
                        />
                      )}
                    </Button>
                    <Menu 
                      anchorEl={anchorEl} 
                      open={Boolean(anchorEl)} 
                      onClose={() => handleToursClose()}
                      TransitionComponent={Fade}
                      transitionDuration={300}
                      PaperProps={{
                        elevation: 8,
                        sx: {
                          mt: 1.5,
                          borderRadius: 3,
                          minWidth: 240,
                          overflow: 'visible',
                          border: '1px solid rgba(0,0,0,0.04)',
                          '&::before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 20,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            borderLeft: '1px solid rgba(0,0,0,0.04)',
                            borderTop: '1px solid rgba(0,0,0,0.04)',
                          },
                        },
                      }}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <Box sx={{ px: 2.5, py: 1.5 }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.secondary',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 1.2,
                            fontSize: '0.65rem'
                          }}
                        >
                          Explore Tours
                        </Typography>
                      </Box>
                      {menuItems
                        .find(item => item.hasDropdown)
                        ?.dropdownItems.map((dropdownItem) => (
                          <MenuItem 
                            key={dropdownItem.value} 
                            onClick={() => handleToursClose(dropdownItem.value)}
                            sx={{ 
                              mx: 1, 
                              borderRadius: 2, 
                              mb: 0.5, 
                              py: 1.2,
                              px: 2,
                              '&:hover': { 
                                bgcolor: 'rgba(12, 35, 64, 0.06)',
                                transform: 'translateX(4px)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>
                              {dropdownItem.icon}
                            </ListItemIcon>
                            <Box>
                              <Typography fontWeight={700} variant="body2">
                                {dropdownItem.label}
                              </Typography>
                              <Typography color="text.secondary" variant="caption">
                                {dropdownItem.desc}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                    </Menu>
                  </Box>
                ) : (
                  <Button
                    key={item.label}
                    component={RouterLink}
                    to={item.path}
                    sx={{
                      color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                      fontWeight: isActive(item.path) ? 700 : 500,
                      px: { md: 1, lg: 1.8 },
                      py: 0.8,
                      borderRadius: 2,
                      fontSize: { md: '0.8rem', lg: '0.9rem' },
                      position: 'relative',
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      '&::after': isActive(item.path) ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 2,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '40%',
                        height: 3,
                        borderRadius: 1.5,
                        bgcolor: 'primary.main',
                      } : {},
                      '&:hover': {
                        bgcolor: 'rgba(12, 35, 64, 0.06)',
                        color: 'primary.main',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {item.label}
                  </Button>
                )
              ))}
            </Box>

            {/* Right Side - CTA Button (Desktop) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0, gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/contact')}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark', transform: 'translateY(-1px)' },
                  px: { md: 2, lg: 3 },
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { md: '0.8rem', lg: '0.85rem' },
                  boxShadow: '0 4px 12px rgba(12, 35, 64, 0.2)',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                Book Now
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer 
        open={mobileOpen} 
        onClose={() => setMobileOpen(false)}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box',
            width: { xs: '100%', sm: 380 },
            maxWidth: 420,
            borderRadius: { xs: 0, sm: '0 20px 20px 0' },
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;